import * as React from "react"
import { Circle, CircleCheck, LoaderCircle, Zap } from "lucide-react"

import { cn } from "@/lib/utils"
import { type BuildTask } from "./types"

/**
 * The interstitial between review and the created automation.
 *
 * NOTE ON THE CHECKLIST: `tasks` is presentation, not telemetry. Creating a
 * route is one API call with no per-task progress signal, so a caller staging
 * these statuses is animating an estimate. Don't read a `done` row as
 * confirmation that a step finished server-side.
 */

/**
 * Spoken equivalent of each status icon. The check / spinner / hollow circle
 * carry the entire meaning of a row visually, so without this "Reading recent
 * items" sounds identical whether it's pending, running or finished.
 */
const BUILD_STATUS_LABEL: Record<BuildTask["status"], string> = {
  done: "done",
  running: "in progress",
  todo: "pending",
}

export function BuildingStep({
  heading,
  tasks,
  subtitle,
}: {
  heading: string
  tasks: BuildTask[]
  subtitle?: string
}) {
  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:items-center tw:gap-4 tw:py-2">
      <span className="tw:mb-2 tw:flex tw:size-16 tw:items-center tw:justify-center tw:rounded-full tw:bg-secondary tw:text-secondary-foreground">
        <Zap className="tw:size-7" />
      </span>
      {/* text-center matters only once the heading wraps: `items-center` centres
          the BOX, so at 303px "Building your automation" filled the width and
          then set its two lines flush left, under a centred badge and above a
          centred subtitle. At desktop it's one line in a content-width box, so
          this is a no-op there. */}
      <h2 className="tw:font-heading tw:text-2xl tw:font-semibold tw:text-balance tw:text-center tw:text-foreground">
        {heading}
      </h2>
      {/* Matches the shell's own subtitle slot — this screen renders its copy
          here instead, and the same string in the same role should wrap the
          same way. */}
      {subtitle ? (
        <p className="tw:max-w-[314px] tw:text-center tw:text-sm tw:text-balance tw:text-muted-foreground">
          {subtitle}
        </p>
      ) : null}
      {/* A live region wrapping the list, rather than role="status" on the <ul>
          itself, which would replace its list semantics. Static here, but prod
          flips these statuses and the transitions need announcing.
          The definite width keeps the block's optical offset stable: sized to
          content, it was as wide as whichever label happened to be longest, so
          the centred block shifted with the data. */}
      <div
        role="status"
        aria-live="polite"
        className="tw:mt-2 tw:w-full tw:max-w-[260px]"
      >
        <ul className="tw:flex tw:flex-col tw:gap-2.5">
          {tasks.map((task) => (
            /* items-start, not items-center: `tasks` is a prop, and a label long
               enough to wrap would otherwise centre the icon against the whole
               block instead of its first line. mt-0.5 centres the 16px icon on
               the 20px first line. */
            <li key={task.label} className="tw:flex tw:items-start tw:gap-2">
              {task.status === "done" ? (
                <CircleCheck
                  className="tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-primary"
                  aria-hidden
                />
              ) : task.status === "running" ? (
                <LoaderCircle
                  className="tw:mt-0.5 tw:size-4 tw:shrink-0 tw:animate-spin tw:text-foreground"
                  aria-hidden
                />
              ) : (
                <Circle
                  className="tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-muted-foreground/50"
                  aria-hidden
                />
              )}
              <span
                className={cn(
                  "tw:text-sm",
                  task.status === "todo"
                    ? "tw:text-muted-foreground"
                    : "tw:text-foreground"
                )}
              >
                {task.label}
                <span className="tw:sr-only">
                  {` — ${BUILD_STATUS_LABEL[task.status]}`}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
